import { useSearchParams } from "react-router-dom";
import { Eye } from "lucide-react";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import useUserPermission from "~/hooks/useUserPermission";
import { AdminPage } from "~/pages/manifold-nextgen/components/admin/AdminPage";
import { QuickSearch } from "~/pages/manifold-nextgen/components/search/QuickSearch";
import { SearchExperience } from "~/pages/manifold-nextgen/components/search/SearchExperience";
import { VIEW_ALL_AI_CONVERSATIONS } from "~/router/permissions";
import { IndexTab } from "./IndexTab";

const TAB_PARAM = "tab";
const TABS = { SEARCH: "search", QUICK: "quick", INDEX: "index" } as const;

/**
 * Semantic search, plus the index behind it for `view:all-ai-conversations`
 * holders, since its surveys name every user's projects and sessions.
 */
export const SearchManager = () => {
  useDocumentTitle("Search Manager");
  const canManageIndex = useUserPermission(VIEW_ALL_AI_CONVERSATIONS);
  // In the URL so a reload keeps the tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get(TAB_PARAM);
  const defaultTab = canManageIndex ? TABS.INDEX : TABS.SEARCH;
  const tab =
    requestedTab === TABS.SEARCH || requestedTab === TABS.QUICK ? requestedTab : defaultTab;

  return (
    <AdminPage
      title="Search Manager"
      description="Search everything you can see, and fill the index behind it"
      width="default"
    >
      <Alert variant="warning" className="mb-4" data-testid="search-manager-preview-banner">
        <Eye />
        <AlertDescription>
          <p>
            You&apos;re in the <code>ai-search-manager</code> flag. Results are only what you can
            access.
            {canManageIndex && " Indexing and pruning change the shared index for everyone."}
          </p>
        </AlertDescription>
      </Alert>

      <Tabs
        value={tab}
        onValueChange={(next) =>
          setSearchParams(
            (current) => {
              const params = new URLSearchParams(current);
              params.set(TAB_PARAM, next);
              return params;
            },
            { replace: true }
          )
        }
      >
        <TabsList variant="underlined" className="mb-4">
          {canManageIndex && <TabsTrigger value={TABS.INDEX}>Index</TabsTrigger>}
          <TabsTrigger value={TABS.SEARCH}>Search</TabsTrigger>
          <TabsTrigger value={TABS.QUICK}>Quick search</TabsTrigger>
        </TabsList>
        <TabsContent value={TABS.SEARCH}>
          <div className="mx-auto w-full max-w-3xl">
            <SearchExperience diagnostics />
          </div>
        </TabsContent>
        <TabsContent value={TABS.QUICK}>
          {/* The search as a homepage hero would host it, with room for its results to open. */}
          <div className="mx-auto min-h-128 w-full max-w-2xl pt-6">
            <QuickSearch
              seeAllHref={(query, scope) =>
                `?${new URLSearchParams({ [TAB_PARAM]: TABS.SEARCH, q: query, ...(scope && { type: scope }) })}`
              }
            />
          </div>
        </TabsContent>
        {canManageIndex && (
          <TabsContent value={TABS.INDEX}>
            <IndexTab />
          </TabsContent>
        )}
      </Tabs>
    </AdminPage>
  );
};
